import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

import { problemDetails } from '@core/utils/problem-details';

/** Statuses that mean "you are signed in but may not touch this resource" — surface a toast, never sign out. */
const ACCESS_DENIED_STATUSES = [401, 403];

/**
 * Shows a toast when a service call is refused for access reasons (401/403). A 401 is deliberately NOT treated as a
 * dead session here: a real session loss is detected by the OIDC `SilentRenewFailed` event (see SessionExpiryService),
 * whereas an API 401/403 usually just means the caller lacks access to that one resource. Injected and called from
 * each service's error handler.
 */
@Injectable({ providedIn: 'root' })
export class PermissionErrorToast {
  // Optional so a service's unit test need not provide MessageService; the real app always has it (app.config).
  private readonly messages = inject(MessageService, { optional: true });

  notifyIfAccessDenied(error: unknown): void {
    if (!this.messages || !(error instanceof HttpErrorResponse) || !ACCESS_DENIED_STATUSES.includes(error.status)) {
      return;
    }
    const message = problemDetails(error).detail ?? "You don't have permission to access this resource.";
    const path = this.enginePath(error);
    // The path rides in `data` (not `detail`) so the toast template can style it small + bold on its own line.
    this.messages.add({
      severity: 'warn',
      summary: 'Access denied',
      detail: message,
      data: path ? { path } : undefined
    });
  }

  /** The engine-relative request path — origin, the gateway `/api` prefix and any query string removed. */
  private enginePath(error: HttpErrorResponse): string | null {
    if (!error.url) {
      return null;
    }
    try {
      return new URL(error.url).pathname.replace(/^\/api\//, '');
    } catch {
      return error.url;
    }
  }
}
